import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, RefreshCw, FileText, Eye, EyeOff } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { useToast } from "../../hooks/use-toast";
import { API, authConfig, axios } from "../../lib/auth";

const CATEGORIES = ["Marketing", "SEO", "Paid Ads", "Social Media", "Web Design", "Content", "Local Business"];
const emptyPost = { title: "", category: "Marketing", author: "Jellis Enterprise", excerpt: "", content: "", cover_image: "", published: true, meta_title: "", meta_description: "" };

const BlogManager = ({ onAuthError }) => {
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPost);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleErr = (err) => { if (err?.response?.status === 401) onAuthError?.(); };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/blog`, authConfig());
      setPosts(res.data);
    } catch (err) { handleErr(err); toast({ title: "Failed to load posts", variant: "destructive" }); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => { setForm(emptyPost); setEditingId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ title: p.title, category: p.category, author: p.author, excerpt: p.excerpt, content: p.content, cover_image: p.cover_image, published: p.published, meta_title: p.meta_title || "", meta_description: p.meta_description || "" });
    setEditingId(p.id);
    setOpen(true);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast({ title: "Title is required", variant: "destructive" }); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/blog/${editingId}`, form, authConfig());
        toast({ title: "Post updated" });
      } else {
        await axios.post(`${API}/admin/blog`, form, authConfig());
        toast({ title: "Post published" });
      }
      setOpen(false);
      fetchPosts();
    } catch (err) { handleErr(err); toast({ title: "Couldn't save post", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/blog/${id}`, authConfig());
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast({ title: "Post deleted" });
    } catch (err) { handleErr(err); toast({ title: "Couldn't delete post", variant: "destructive" }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Blog posts</h2>
          <p className="text-sm text-slate-500">{posts.length} total — create, edit, publish or unpublish articles.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchPosts} variant="outline" className="font-semibold">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={openNew} className="brand-gradient text-white font-semibold">
            <Plus className="mr-2 h-4 w-4" /> New post
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-16 text-center text-slate-400">Loading posts...</div>
      ) : posts.length === 0 ? (
        <div className="p-16 text-center rounded-2xl bg-white border border-slate-200">
          <FileText className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-4 text-slate-500 font-medium">No posts yet.</p>
          <Button onClick={openNew} className="mt-4 brand-gradient text-white font-semibold"><Plus className="mr-2 h-4 w-4" />Write your first post</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {posts.map((p) => (
            <div key={p.id} className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
              <div className="relative h-36">
                {p.cover_image ? (
                  <img src={p.cover_image} alt={p.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-slate-100 flex items-center justify-center"><FileText className="h-8 w-8 text-slate-300" /></div>
                )}
                <Badge className={`absolute top-3 left-3 font-semibold ${p.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"} hover:bg-current/10`}>
                  {p.published ? <><Eye className="h-3 w-3 mr-1" />Published</> : <><EyeOff className="h-3 w-3 mr-1" />Draft</>}
                </Badge>
              </div>
              <div className="p-4">
                <Badge variant="secondary" className="text-xs font-medium">{p.category}</Badge>
                <h3 className="mt-2 font-display font-bold text-slate-900 leading-snug line-clamp-2">{p.title}</h3>
                <p className="mt-1 text-sm text-slate-500 line-clamp-2">{p.excerpt}</p>
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="flex-1 font-semibold"><Pencil className="mr-1.5 h-3.5 w-3.5" />Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editingId ? "Edit post" : "New post"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input id="title" value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="e.g. 5 Local SEO Tips for 2025" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => update("category", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="author">Author</Label>
                <Input id="author" value={form.author} onChange={(e) => update("author", e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cover">Cover image URL</Label>
              <Input id="cover" value={form.cover_image} onChange={(e) => update("cover_image", e.target.value)} placeholder="https://..." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="excerpt">Excerpt (short summary)</Label>
              <Textarea id="excerpt" rows={2} value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} placeholder="One or two sentences shown on the blog list." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <Textarea id="content" rows={8} value={form.content} onChange={(e) => update("content", e.target.value)} placeholder="Write your article here. Separate paragraphs with a blank line." />
            </div>
            <div className="rounded-xl border border-slate-200 p-4 space-y-4">
              <p className="text-sm font-bold text-slate-700 flex items-center gap-2">SEO &amp; social sharing <span className="text-xs font-normal text-slate-400">(optional)</span></p>
              <div className="space-y-2">
                <Label htmlFor="metatitle">Meta title</Label>
                <Input id="metatitle" value={form.meta_title} onChange={(e) => update("meta_title", e.target.value)} placeholder="Defaults to the post title if left blank" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="metadesc">Meta description</Label>
                <Textarea id="metadesc" rows={2} value={form.meta_description} onChange={(e) => update("meta_description", e.target.value)} placeholder="Shown in Google results & social previews. Defaults to the excerpt." />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-slate-200 p-4">
              <div>
                <Label htmlFor="published" className="font-semibold">Published</Label>
                <p className="text-sm text-slate-500">Toggle off to save as a draft (hidden from the public blog).</p>
              </div>
              <Switch id="published" checked={form.published} onCheckedChange={(v) => update("published", v)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="brand-gradient text-white font-semibold" onClick={save} disabled={saving}>
              {saving ? "Saving..." : editingId ? "Save changes" : "Publish post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManager;
